import { Check, BookOpen, Sparkles, ArrowRight, Shield, Download } from "lucide-react";
import { featuredProducts } from "@/data/featuredProducts";

function ProductHero({ p }) {
    return (
        <div
            className="relative rounded-[2.5rem] overflow-hidden bg-gradient-to-br from-ink-900 via-ink-900 to-brand-950 text-white"
            data-testid={`featured-product-${p.id}`}
        >
            <div className="absolute -top-32 -right-20 h-[320px] w-[320px] rounded-full bg-brand-500/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-20 h-[280px] w-[280px] rounded-full bg-pinky-500/20 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-0 items-stretch">
                <div className="lg:col-span-5 relative">
                    {p.badge && (
                        <div className="absolute top-6 left-6 z-10 inline-flex items-center gap-2 rounded-full bg-pinky-500 text-white text-[11px] font-semibold uppercase tracking-[0.18em] px-3.5 py-1.5 shadow-lg">
                            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                            {p.badge}
                        </div>
                    )}
                    {p.tag && (
                        <div className="absolute top-6 right-6 z-10 inline-flex items-center gap-1 rounded-full bg-white/95 text-ink-900 text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 shadow">
                            {p.tag}
                        </div>
                    )}
                    <div className="relative h-full min-h-[420px] lg:min-h-[600px] overflow-hidden bg-ink-950 flex items-center justify-center p-6 sm:p-10">
                        <img
                            src={p.cover}
                            alt={`${p.name} — couverture`}
                            className="relative max-h-[440px] lg:max-h-[520px] w-auto object-contain rounded-xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)]"
                            loading="lazy"
                        />
                    </div>
                </div>

                <div className="lg:col-span-7 p-8 sm:p-12 lg:p-14 relative">
                    <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-300">
                        <BookOpen className="h-4 w-4" strokeWidth={2} />
                        {p.eyebrow}
                    </div>

                    <h3 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
                        {p.nameAccent ? (
                            <>
                                {p.name.replace(p.nameAccent, "").trim()}{" "}
                                <span className="text-brand-300">{p.nameAccent}</span>
                            </>
                        ) : (
                            p.name
                        )}
                    </h3>

                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink-200 max-w-xl">
                        {p.description}
                    </p>

                    {p.benefits?.length > 0 && (
                        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                            {p.benefits.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-sm text-ink-100">
                                    <span className="mt-0.5 grid place-items-center h-5 w-5 rounded-full bg-brand-500 shrink-0">
                                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                                    </span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="mt-10 flex flex-wrap items-end gap-5">
                        <div>
                            {p.priceCaption && (
                                <p className="text-xs uppercase tracking-[0.18em] text-brand-300 font-semibold">
                                    {p.priceCaption}
                                </p>
                            )}
                            <div className="mt-2 flex items-baseline gap-3">
                                <span className="font-display font-bold text-5xl sm:text-6xl tracking-tight text-white">
                                    {p.price}
                                </span>
                                {p.oldPrice && (
                                    <span className="font-display text-2xl text-ink-400 line-through decoration-pinky-500 decoration-2">
                                        {p.oldPrice}
                                    </span>
                                )}
                                {p.discountLabel && (
                                    <span className="inline-flex items-center rounded-full bg-pinky-500/20 text-pinky-300 text-xs font-bold px-2.5 py-1">
                                        {p.discountLabel}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <a
                            href={p.cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`featured-product-cta-${p.id}`}
                            className="btn-fire group"
                        >
                            {p.cta.label}
                            <ArrowRight
                                className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
                                strokeWidth={2.5}
                            />
                        </a>
                        <span className="inline-flex items-center gap-1.5 text-xs text-ink-300">
                            <Download className="h-3.5 w-3.5" strokeWidth={2} />
                            Téléchargement immédiat
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-xs text-ink-300">
                            <Shield className="h-3.5 w-3.5" strokeWidth={2} />
                            Paiement sécurisé · Payhip
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProductCard({ p }) {
    return (
        <article
            data-testid={`featured-product-${p.id}`}
            className="relative soft-card overflow-hidden flex flex-col group transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-30px_rgba(20,184,166,0.4)]"
        >
            <div className="relative bg-ink-950 flex items-center justify-center p-6 aspect-[4/5] overflow-hidden">
                {p.badge && (
                    <div className="absolute top-5 left-5 z-10 inline-flex items-center gap-1.5 rounded-full bg-pinky-500 text-white text-[10px] font-semibold uppercase tracking-[0.18em] px-3 py-1.5 shadow-lg">
                        <Sparkles className="h-3 w-3" strokeWidth={2.5} />
                        {p.badge}
                    </div>
                )}
                {p.tag && (
                    <div className="absolute top-5 right-5 z-10 inline-flex items-center rounded-full bg-white/95 text-ink-900 text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1.5 shadow">
                        {p.tag}
                    </div>
                )}
                <img
                    src={p.cover}
                    alt={`${p.name} — couverture`}
                    className="max-h-full w-auto object-contain rounded-lg shadow-[0_20px_40px_-10px_rgba(0,0,0,0.5)] transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                />
            </div>
            <div className="p-7 sm:p-8 flex-1 flex flex-col">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-600">
                    <BookOpen className="h-3.5 w-3.5" strokeWidth={2} />
                    {p.eyebrow}
                </p>
                <h3 className="font-display font-semibold text-xl text-ink-900 leading-snug mt-3">
                    {p.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-600">{p.description}</p>

                <div className="mt-auto pt-6 flex items-end justify-between gap-4">
                    <div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-display font-bold text-3xl text-ink-900">{p.price}</span>
                            {p.oldPrice && (
                                <span className="font-display text-base text-ink-400 line-through decoration-pinky-500 decoration-2">
                                    {p.oldPrice}
                                </span>
                            )}
                        </div>
                        {p.priceCaption && (
                            <p className="text-[10px] uppercase tracking-[0.16em] text-brand-600 font-semibold mt-1">
                                {p.priceCaption}
                            </p>
                        )}
                    </div>
                    <a
                        href={p.cta.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`featured-product-cta-${p.id}`}
                        className="btn-primary text-sm shrink-0"
                    >
                        {p.cta.label}
                        <ArrowRight className="ml-1.5 h-4 w-4" strokeWidth={2.5} />
                    </a>
                </div>
            </div>
        </article>
    );
}

export default function FeaturedProducts() {
    if (!featuredProducts.length) return null;

    const single = featuredProducts.length === 1;
    const titleLabel = single ? "Notre ressource à la une" : "Nos ressources à la une";
    const subtitle = single ? (
        <>Notre <span className="text-brand-500">produit vedette</span>.</>
    ) : (
        <>Nos <span className="text-brand-500">produits vedettes</span>.</>
    );

    return (
        <section
            id="produit-vedette"
            data-testid="featured-products-section"
            className="py-24 sm:py-32 bg-white relative overflow-hidden"
        >
            <div className="pointer-events-none absolute -top-20 right-0 h-[420px] w-[420px] rounded-full bg-pinky-100/50 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 h-[420px] w-[420px] rounded-full bg-brand-100/60 blur-3xl" />

            <div className="container-wide relative">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <span className="eyebrow-pink">{titleLabel}</span>
                    <h2 data-testid="featured-products-title" className="display-2 mt-4">
                        {subtitle}
                    </h2>
                </div>

                {single ? (
                    <ProductHero p={featuredProducts[0]} />
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featuredProducts.map((p) => (
                            <ProductCard key={p.id} p={p} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
